import React, { useState, useEffect, useRef, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import $ from 'jquery';
import 'select2/dist/css/select2.min.css';
import 'select2';

const ReferralUsersList = ({ businessId, token, branches = [] }) => {
    const [loading, setLoading] = useState(false);
    const [usersData, setUsersData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('all');
    const branchSelectRef = useRef(null);
    const isBranchSelectInitialized = useRef(false);
    const isInitialMount = useRef(true);

    const fetchUsers = useCallback(async () => {
        if (!businessId) return;
        
        setLoading(true);
        try {
            let url = `${process.env.REACT_APP_BASE_URL}/referral/users?business_id=${businessId}&page=${currentPage}&limit=${rowsPerPage}`;
            
            if (searchTerm && searchTerm.trim() !== '') {
                url += `&search=${encodeURIComponent(searchTerm)}`;
            }
            
            if (selectedBranch && selectedBranch !== 'all') {
                url += `&branch_id=${selectedBranch}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) throw new Error('Failed to fetch users');
            
            const data = await response.json();
            setUsersData(data.data || []);
            setTotalRows(data.total || 0);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    }, [businessId, currentPage, rowsPerPage, searchTerm, selectedBranch, token]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            fetchUsers();
        } else {
            fetchUsers();
        }
    }, [fetchUsers]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (newRowsPerPage, page) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(page);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleBranchChange = (e) => {
        const branchId = e.target.value;
        setSelectedBranch(branchId);
        setCurrentPage(1);
    };

    useEffect(() => {
        if (branchSelectRef.current && branches && branches.length > 0 && !isBranchSelectInitialized.current) {
            if ($(branchSelectRef.current).data('select2')) {
                $(branchSelectRef.current).select2('destroy');
            }
            
            $(branchSelectRef.current).select2({
                placeholder: 'All Branches',
                allowClear: true,
                width: '100%'
            }).off('change').on('change', handleBranchChange);
            
            isBranchSelectInitialized.current = true;
        }
        
        return () => {
            if (branchSelectRef.current && isBranchSelectInitialized.current) {
                try {
                    if ($(branchSelectRef.current).data('select2')) {
                        $(branchSelectRef.current).select2('destroy');
                    }
                } catch (err) {
                    console.warn('Error destroying select2:', err);
                }
                isBranchSelectInitialized.current = false;
            }
        };
    }, [branches]);

    const columns = [
        { name: 'ID', selector: row => row.user_id, sortable: true, width: '80px' },
        { name: 'Name', selector: row => row.name, sortable: true },
        { name: 'Email', selector: row => row.email, sortable: true },
        { name: 'Phone', selector: row => row.contact || '-', sortable: true },
        { name: 'Branch', selector: row => row.branch_name || '-', sortable: true },
        { 
            name: 'Created Date', 
            selector: row => row.created_at,
            sortable: true,
            cell: row => row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'
        }
    ];

    const csvData = usersData.map(row => ({
        'ID': row.user_id,
        'Name': row.name,
        'Email': row.email,
        'Phone': row.contact || '-',
        'Branch': row.branch_name || '-',
        'Created Date': row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'
    }));

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="content">
            <div className="col-sm-12 print-hidden">
                <div className="card-box">
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label className="control-label">Filter by Branch:</label>
                                <select 
                                    ref={branchSelectRef}
                                    className="form-control"
                                    defaultValue="all"
                                >
                                    <option value="all">All Branches</option>
                                    {branches && branches.map((branch) => (
                                        <option key={branch.id_branch} value={branch.id_branch}>
                                            {branch.branch_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    
                    <div className='col-12' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', marginTop: '30px' }}>
                        <div>
                            <CSVLink
                                data={csvData}
                                filename={`referral_users_${new Date().toISOString().split('T')[0]}.csv`}
                                className="btn btn-primary print-hidden"
                                style={{ margin: '2px' }}
                            >
                                CSV
                            </CSVLink>
                            <button className="btn btn-primary print-hidden" onClick={handlePrint} style={{ margin: '2px' }}>
                                Print
                            </button>
                        </div>
                        <input
                            type="text"
                            className='form-control print-hidden'
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            style={{ width: '200px' }}
                        />
                    </div>
                    
                    <div className='col-12'>
                        <DataTable
                            columns={columns}
                            data={usersData}
                            progressPending={loading}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handleRowsPerPageChange}
                            paginationRowsPerPageOptions={[10, 15, 20, 25, 30, 50, 100]}
                            striped
                            responsive
                            noDataComponent="No users found."
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReferralUsersList;